
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import DataTable from "@/components/admin/DataTable";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";

interface ProposalUsageStat {
  user_id: string;
  user_email: string;
  proposal_count: number;
  last_generated: string;
}

const ProposalUsageAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ProposalUsageStat[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        
        // Call the database function to get proposal usage stats
        const { data, error } = await supabase.rpc('get_proposal_usage_stats');
        
        if (error) {
          throw error;
        }

        setStats(data || []);
        
        // Calculate total proposals generated
        const total = data?.reduce((sum: number, item: ProposalUsageStat) => sum + item.proposal_count, 0) || 0;
        setTotalCount(total);
      } catch (err) {
        console.error("Error fetching proposal usage stats:", err);
        setError("Failed to load proposal usage statistics");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const columns = [
    {
      key: "user_email",
      label: "User Email"
    },
    {
      key: "proposal_count",
      label: "Proposals Generated",
      format: (value: number) => (
        <Badge variant="outline" className="font-mono">
          {value}
        </Badge>
      )
    },
    {
      key: "last_generated",
      label: "Last Generated",
      format: (value: string) => format(new Date(value), 'MMM d, yyyy h:mm a')
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Error</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-destructive">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Proposal Generation Statistics</CardTitle>
          <CardDescription>
            Track how many proposals users have generated with the AI tool
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-base font-medium">Total Users</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{stats.length}</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-base font-medium">Total Proposals</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{totalCount}</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-base font-medium">Avg. Per User</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">
                  {stats.length ? (totalCount / stats.length).toFixed(1) : '0'}
                </p>
              </CardContent>
            </Card>
          </div>

          <DataTable
            data={stats}
            columns={columns}
            idField="user_id"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ProposalUsageAdmin;
